import React, { useEffect, useState } from 'react';
import { useQuery } from '@apollo/client';
import ThirdPane from '../ThirdPane';
import ThirdPaneContent from '../ThirdPane/ThirdPaneContent';
import ThirdPaneHeader, {
  ThirdPaneHeaderProps,
} from '../ThirdPane/ThirdPaneHeader';
import CompanyDetail from './CompanyDetail';
import { Company, GET_COMPANY } from './CompanyThirdPane.queries';
import { PortalWithState } from 'react-portal';
import Button from '../Button/Button';
import CompanyEditModal from './CompanyEditModal';

type CompanyThirdPaneProps = Partial<ThirdPaneHeaderProps> & {
  id?: string;
  reload?: () => void;
};

export default function CompanyThirdPane({
  id,
  onPrev,
  onNext,
  onToggle,
  prevEnabled,
  nextEnabled,
}: CompanyThirdPaneProps): JSX.Element {
  const {
    error: itemError,
    data: itemData,
    refetch,
  } = useQuery<{
    companies: Company[];
  }>(GET_COMPANY, {
    skip: !id,
    variables: { where: { id } },
  });

  const [company, setWaterSupplier] = useState<Company>();

  useEffect(() => {
    if (itemData?.companies?.length) {
      setWaterSupplier(itemData?.companies[0]);
    }
  }, [id, itemData, setWaterSupplier]);

  if (itemError) {
    throw itemError;
  }

  return (
    <ThirdPane visible={!!id}>
      <ThirdPaneHeader
        onToggle={onToggle}
        title={company?.id ?? 'Loading...'}
        onPrev={onPrev}
        onNext={onNext}
        prevEnabled={prevEnabled}
        nextEnabled={nextEnabled}
      >
        {company && (
          <PortalWithState closeOnEsc>
            {({ openPortal, closePortal, portal }) => (
              <>
                <Button
                  mr={10}
                  variant="outline"
                  icon="edit"
                  onClick={openPortal}
                >
                  Edit
                </Button>
                {portal(
                  <CompanyEditModal
                    action="edit"
                    onClose={() => {
                      closePortal();
                      refetch();
                    }}
                    company={{
                      profileId: company.id,
                      profile: {
                        ...company,
                        type: 'COMPANY',
                      },
                      user: {
                        email: company.user.email,
                      },
                      isBackflowTester: !!company.backflowTester,
                      waterSupplierIds: company?.waterSupplierIds,
                      isWaterSupplier: !!company.waterSupplier,
                    }}
                  />
                )}
              </>
            )}
          </PortalWithState>
        )}
      </ThirdPaneHeader>
      <ThirdPaneContent>
        {company && <CompanyDetail company={company} />}
      </ThirdPaneContent>
    </ThirdPane>
  );
}
